"use client";

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
} from "react";
import dynamic from "next/dynamic";
import { useInView, useReducedMotion } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import type MuxPlayerElement from "@mux/mux-player";
import type { GROQMuxVideo } from "@/sanity/lib/definitions";
import type {
  AiDemoMediaType,
  AiMedia,
} from "@/sanity/pages/PageAiFeaturesTemplate";
import { ProductImage } from "./ui";
import styles from "./product-demos.module.scss";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
});

export default function DemoMedia({
  children,
  label,
  mode,
  video,
  image,
  mobileImage,
  sizes,
  className = "",
}: {
  children: ReactNode;
  label: string;
  mode?: AiDemoMediaType;
  video?: GROQMuxVideo;
  image?: AiMedia;
  mobileImage?: AiMedia;
  sizes: string;
  className?: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const player = useRef<MuxPlayerElement>(null);
  const animations = useRef<Animation[]>([]);
  const inView = useInView(root, { amount: 0.28 });
  const nearView = useInView(root, { once: true, margin: "600px" });
  const reducedMotion = useReducedMotion();
  const [visible, setVisible] = useState(true);
  const [ended, setEnded] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const playbackId = stegaClean(video?.playbackId || "");
  const mediaMode = stegaClean(mode);
  const showingVideo = mediaMode === "video" && !!playbackId && !videoFailed;
  const showingImage = mediaMode === "image" && !!image?.src;
  const animated = !showingImage && !showingVideo && !reducedMotion;
  const awake = inView && visible;

  useEffect(() => setVideoFailed(false), [playbackId]);

  useEffect(() => {
    const update = () => setVisible(document.visibilityState === "visible");
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    if (!animated || !root.current) return;
    setEnded(false);
    const loop = !!root.current.querySelector("[data-demo-loop]");
    const nodes = [
      ...root.current.querySelectorAll<HTMLElement | SVGElement>(
        "[data-demo-at]",
      ),
    ];
    const duration = Math.max(
      0,
      ...nodes.map(
        (node) =>
          (Number(node.dataset.demoAt) || 0) +
          (Number(node.dataset.demoDuration) || 1000),
      ),
    );
    const cycle = loop ? Math.max(4000, duration) + 3320 : duration + 400;
    const sequence = nodes.map((node) => {
      const delay = Number(node.dataset.demoAt) || 0;
      const length = Number(node.dataset.demoDuration) || 1000;
      const kind = node.dataset.demoMotion;
      let frames: Keyframe[] = [
        { opacity: 0, transform: "translateY(9px)" },
        { opacity: 1, transform: "translateY(0)" },
      ];
      if (kind === "bar")
        frames = [{ transform: "scaleY(0)" }, { transform: "scaleY(1)" }];
      if (kind === "ring")
        frames = [{ strokeDashoffset: "100" }, { strokeDashoffset: "33" }];
      if (loop) {
        const from = { ...frames[0], opacity: 0 };
        const to = { ...frames[1], opacity: 1 };
        frames = [
          { ...from, offset: 0 },
          {
            ...from,
            offset: delay / cycle,
            easing: "cubic-bezier(0.16, 1, 0.3, 1)",
          },
          { ...to, offset: (delay + length) / cycle },
          {
            ...to,
            offset: (cycle - 320) / cycle,
            easing: "cubic-bezier(.4,0,1,1)",
          },
          { ...to, opacity: 0, offset: 1 },
        ];
      }
      const animation = node.animate(frames, {
        delay: loop ? 0 : delay,
        duration: loop ? cycle : length,
        iterations: loop ? Infinity : 1,
        fill: "both",
        easing: loop ? "linear" : "cubic-bezier(0.16, 1, 0.3, 1)",
      });
      animation.pause();
      return animation;
    });
    const clock = root.current.animate([], {
      duration: cycle,
      iterations: loop ? Infinity : 1,
    });
    clock.pause();
    clock.onfinish = () => setEnded(true);
    animations.current = [...sequence, clock];
    return () => {
      animations.current.forEach((animation) => animation.cancel());
      animations.current = [];
    };
  }, [animated]);

  useEffect(() => {
    animations.current.forEach((animation) => {
      if (awake && !ended) {
        if (
          Number(animation.currentTime) <
          Number(animation.effect?.getComputedTiming().endTime)
        )
          animation.play();
      } else animation.pause();
    });
    if (!awake) player.current?.pause();
  }, [awake, ended, animated, showingVideo]);

  return (
    <div
      ref={root}
      className={styles.media + " " + className}
      style={
        {
          "--demo-aspect":
            image?.width && image?.height
              ? image.width / image.height
              : undefined,
          "--demo-mobile-aspect":
            mobileImage?.width && mobileImage?.height
              ? mobileImage.width / mobileImage.height
              : undefined,
        } as CSSProperties
      }
      data-product-demo={label}
      data-media-near={nearView}
      data-demo-playing={animated && awake && !ended}
    >
      {showingImage ? (
        <ProductImage image={image} mobileImage={mobileImage} sizes={sizes} />
      ) : showingVideo ? (
        <MuxPlayer
          ref={player}
          className={styles.video}
          playbackId={playbackId}
          streamType="on-demand"
          title={label}
          poster={image?.src}
          preload="none"
          playsInline
          accentColor="#ff5500"
          disableTracking
          disableCookies
          onError={() => setVideoFailed(true)}
        />
      ) : (
        <>
          <div
            className={styles.scene}
            role="img"
            aria-label={label + ". Product demonstration."}
          >
            <div aria-hidden="true">{children}</div>
          </div>
          {videoFailed && (
            <div className={styles.playback} role="status">
              Video unavailable · Demo preview
            </div>
          )}
        </>
      )}
    </div>
  );
}
